// @react-compiler enable
import {testIdProps} from '@github-ui/test-id-props'
import {PlusIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {memo, use, useEffect, useRef} from 'react'

import {SystemColumnId} from '../../../api/columns/contracts/memex-column'
import type {TitleValueWithContentType} from '../../../api/columns/contracts/title'
import {ItemType} from '../../../api/memex-items/item-type'
import {useTableHierarchy} from '../../../features/sub-issues/use-table-hierarchy'
import {isNumber, parseTitleNumber} from '../../../helpers/parsing'
import {useTableSidePanel} from '../../../hooks/use-side-panel'
import {type ColumnValue, hasValue} from '../../../models/column-value'
import type {MemexItemModel} from '../../../models/memex-item-model'
import {MemexItemIcon} from '../../common/memex-item-icon'
import {TextPlaceholder} from '../../common/placeholders'
import {InteractiveItemTitle} from '../../interactive-item-title'
import {BaseCell} from '../cells/base-cell'
import {ExpandButton, type ExpandButtonProps} from '../expand-button'
import {focusSubIssueOmnibar, TableFocusType, useStableTableNavigation, useTableNavigation} from '../navigation'
import {useRecordCellRenderer} from '../performance-measurements'
import {RowContext} from '../table-row'
import styles from './title-renderer.module.css'

type Props = Readonly<
  {
    currentValue: ColumnValue<TitleValueWithContentType>
    model: MemexItemModel
    isDisabled?: boolean
    rowId: string
    /** If defined, represents the number of levels deep this item is in a sub-issues hierarchy. */
    subIssueDepth?: number
  } & Omit<ExpandButtonProps, 'issueId'>
>

export const LoadingTitleCell = () => (
  <BaseCell>
    <TextPlaceholder minWidth={80} maxWidth={200} {...testIdProps('placeholder')} />
  </BaseCell>
)

const AddSubIssueComponent = memo(function AddSubIssueComponent({rowId}: {rowId: string}) {
  const {itemData} = use(RowContext)
  const {navigationDispatch} = useStableTableNavigation()
  const {state} = useTableNavigation()
  const buttonRef = useRef<HTMLButtonElement>(null)

  const onAddSubIssue = () => {
    itemData?.setRowIdWithOmnibar(rowId)
    navigationDispatch(focusSubIssueOmnibar(rowId, SystemColumnId.Title))
  }

  // Use effect to focus the add sub-issue button when navigation state changes to it
  useEffect(() => {
    if (state.focus?.type === TableFocusType.ADD_SUB_ISSUE_BUTTON && state.focus.details?.parentY === rowId) {
      buttonRef.current?.focus()
    }
  }, [state.focus, rowId])

  return (
    <div className={styles.PlusIconContainer}>
      <IconButton
        ref={buttonRef}
        icon={PlusIcon}
        aria-label="Add sub-issue"
        size="small"
        onClick={e => {
          e.stopPropagation()
          onAddSubIssue()
        }}
        onKeyDown={e => {
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation()
            onAddSubIssue()
          }
        }}
      />
    </div>
  )
})

export const TitleRenderer = memo(function TitleRenderer({
  currentValue,
  model,
  isDisabled,
  subIssueDepth = 0,
  expandable,
  expanded,
  rowId,
  expandRef,
}: Props) {
  useRecordCellRenderer('TitleRenderer', model.id)
  const titleValue = hasValue(currentValue) ? currentValue.value : undefined
  const number = parseTitleNumber(titleValue)
  const {openPane} = useTableSidePanel()
  const {hierarchyEnabled} = useTableHierarchy()
  const issueId = model.contentType === 'Issue' ? model.content.id.toString() : undefined

  return (
    <BaseCell className={styles.titleCell}>
      {hierarchyEnabled && (
        <div
          style={{'--hierarchy-depth': subIssueDepth, '--expandable': expandable ? 1 : 0}}
          className={styles.hierarchyDepth}
        >
          <ExpandButton
            expandRef={expandRef}
            expanded={expanded}
            expandable={expandable}
            issueId={issueId}
            nwoReference={model.getNameWithOwnerReference()}
            rowId={rowId}
          />
        </div>
      )}
      <div className={styles.Box}>
        <MemexItemIcon titleColumn={currentValue} isBlocked={model.isBlocked()} />
      </div>
      <InteractiveItemTitle model={model} isDisabled={isDisabled} currentValue={currentValue} openPane={openPane} />
      {isNumber(number) && (
        <div className={styles.Box_1}>
          <span className={styles.Text}> #{number}</span>
        </div>
      )}
      {hierarchyEnabled && model.contentType === ItemType.Issue && <AddSubIssueComponent rowId={rowId} />}
    </BaseCell>
  )
})

try{ LoadingTitleCell.displayName ||= 'LoadingTitleCell' } catch {}
try{ AddSubIssueComponent.displayName ||= 'AddSubIssueComponent' } catch {}
try{ TitleRenderer.displayName ||= 'TitleRenderer' } catch {}